import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type { MealSource, CreateMealLogRequest, CustomFoodItem } from '@/types/journal-api';
import { useFoodCategories } from '@/services/categories';
import { RecipeSelector } from './RecipeSelector';
import { fromLocalDateStr, todayLocalDateStr } from '@/lib/date';
import { mealTypeForTime, timeOfDay } from '@/lib/meal-type';
import { Plus, Minus, Loader2, Save, X, AlertCircle, ChefHat } from 'lucide-react';

interface Props {
    onSubmit: (data: CreateMealLogRequest) => void;
    onCancel?: () => void;
    isSubmitting?: boolean;
    /**
     * The local calendar day (YYYY-MM-DD) the meal is for: the day selected on
     * the page that opened the form. Defaults to today. The meal used to be
     * stamped with today whatever day was being viewed (#368).
     */
    initialDate?: string;
}

export const MealLogForm: React.FC<Props> = ({
    onSubmit,
    onCancel,
    isSubmitting = false,
    initialDate,
}) => {
    const today = todayLocalDateStr();
    // Every field starts with a value: the Radix select warns when its value
    // goes from undefined to defined, and the meal type is picked from the
    // time of day so most meals need no choice at all (#376).
    const [initialTime] = useState(() => timeOfDay());
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<Record<string, any>>({
        defaultValues: {
            dateEaten: initialDate ?? today,
            timeEaten: initialTime,
            mealType: mealTypeForTime(initialTime),
        },
    });
    const {
        data: foodCategories = [],
        isLoading: categoriesLoading,
        error: categoriesError,
    } = useFoodCategories();

    const [, setMealSource] = useState<MealSource>('CUSTOM');
    const [customFoodItems, setCustomFoodItems] = useState<CustomFoodItem[]>([
        { categoryId: '', estimatedServings: 1, description: '' },
    ]);
    const [showRecipeSelector, setShowRecipeSelector] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState<any>(null);
    const [recipeFoodItems, setRecipeFoodItems] = useState<CustomFoodItem[]>([]);
    const [foodItemsError, setFoodItemsError] = useState<string | null>(null);

    const watchedMealType: string = watch('mealType') ?? '';
    const watchedTimeEaten: string = watch('timeEaten') ?? '';
    // Until the client picks a meal type, it follows the time eaten.
    const mealTypeChosen = useRef(false);
    useEffect(() => {
        if (mealTypeChosen.current || !watchedTimeEaten) return;
        setValue('mealType', mealTypeForTime(watchedTimeEaten), { shouldValidate: true });
    }, [watchedTimeEaten, setValue]);

    // Handle recipe selection
    const handleRecipeSelect = (recipe: any) => {
        setSelectedRecipe(recipe);
        setShowRecipeSelector(false);

        // Auto-populate food items from recipe nutrition categories
        if (recipe.nutritionCategories && recipe.nutritionCategories.length > 0) {
            const items: CustomFoodItem[] = recipe.nutritionCategories.map((nc: any) => ({
                categoryId: nc.category.id,
                estimatedServings: nc.servings,
                description: `${nc.category.name} from ${recipe.name}`,
            }));
            setRecipeFoodItems(items);
        } else {
            // If no nutrition categories, start with empty
            setRecipeFoodItems([{ categoryId: '', estimatedServings: 1, description: '' }]);
        }

        // Set recipe name in form
        setValue('customMealName', recipe.name);
    };

    const clearRecipeSelection = () => {
        setSelectedRecipe(null);
        setRecipeFoodItems([]);
        setValue('customMealName', '');
    };

    const addFoodItem = () => {
        setCustomFoodItems([
            ...customFoodItems,
            { categoryId: '', estimatedServings: 1, description: '' },
        ]);
    };

    const removeFoodItem = (index: number) => {
        if (selectedRecipe) {
            setRecipeFoodItems(recipeFoodItems.filter((_, i) => i !== index));
        } else {
            setCustomFoodItems(customFoodItems.filter((_, i) => i !== index));
        }
    };

    const updateFoodItem = (index: number, field: keyof CustomFoodItem, value: any) => {
        if (selectedRecipe) {
            const updated = [...recipeFoodItems];
            updated[index] = { ...updated[index], [field]: value };
            setRecipeFoodItems(updated);
        } else {
            const updated = [...customFoodItems];
            updated[index] = { ...updated[index], [field]: value };
            setCustomFoodItems(updated);
        }
    };

    const calculateEstimatedPoints = () => {
        const items = selectedRecipe ? recipeFoodItems : customFoodItems;
        return items.reduce((total, item) => {
            const category = foodCategories.find((c) => c.id === item.categoryId);
            return total + (category ? category.points * item.estimatedServings : 0);
        }, 0);
    };

    const onFormSubmit = (formData: any) => {
        const now = new Date();
        const timeEaten: string = formData.timeEaten || now.toTimeString().slice(0, 5);
        const [hours, minutes] = timeEaten.split(':').map(Number);
        // Local calendar day plus local wall-clock time, so the instant sent to
        // the backend falls on the day the client picked, in their timezone.
        const dateTime = fromLocalDateStr(formData.dateEaten || today);
        dateTime.setHours(hours, minutes, 0, 0);

        // A row at 0 servings means "I did not eat this", the same as removing
        // it, so it is dropped rather than logged as a zero (#367).
        const itemsToSubmit = (selectedRecipe ? recipeFoodItems : customFoodItems).filter(
            (item) =>
                item.categoryId &&
                item.estimatedServings > 0 &&
                // Recipe rows arrive described; only custom rows need one typed.
                (selectedRecipe || item.description.trim()),
        );

        if (itemsToSubmit.length === 0) {
            setFoodItemsError(
                selectedRecipe
                    ? 'Keep at least one food group with servings above 0 to log this meal.'
                    : 'Add at least one food item with a category, servings and a description.',
            );
            return;
        }
        setFoodItemsError(null);

        onSubmit({
            timeEaten: dateTime.toISOString(),
            mealType: formData.mealType,
            mealSource: selectedRecipe ? 'RECIPE' : 'CUSTOM',
            ...(selectedRecipe
                ? {
                      recipeId: selectedRecipe.id,
                      servingsConsumed: 1, // Can be made editable later
                  }
                : {}),
            customMealName: formData.customMealName,
            customFoodItems: itemsToSubmit,
            notes: formData.notes,
        });
    };

    return (
        <Card className="p-6">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground">Log Meal</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Track your meals and earn Light Points
                </p>
                {categoriesError && (
                    <div className="mt-3 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center">
                        <AlertCircle className="w-4 h-4 text-destructive mr-2" />
                        <p className="text-sm text-destructive">
                            Unable to load food categories. Some features may not work properly.
                        </p>
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
                {/* Meal Type */}
                <div className="space-y-2">
                    <Label htmlFor="mealType" className="text-sm font-medium">
                        Meal Type <span className="text-destructive">*</span>
                    </Label>
                    {/* The Select is not an input react-hook-form can bind to, so the
                        value lives on this hidden field. Without it the field is
                        unregistered, the required rule below never runs, and a meal
                        submitted with no type posts mealType: undefined — which the
                        backend rejects with a 400 the client never surfaces. */}
                    <input
                        type="hidden"
                        {...register('mealType', { required: 'Meal type is required' })}
                    />
                    <Select
                        value={watchedMealType}
                        onValueChange={(value) => {
                            mealTypeChosen.current = true;
                            setValue('mealType', value, { shouldValidate: true });
                        }}
                    >
                        <SelectTrigger id="mealType" aria-required="true">
                            <SelectValue placeholder="Select meal type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="BREAKFAST">Breakfast</SelectItem>
                            <SelectItem value="LUNCH">Lunch</SelectItem>
                            <SelectItem value="DINNER">Dinner</SelectItem>
                            <SelectItem value="SNACK">Snack</SelectItem>
                        </SelectContent>
                    </Select>
                    {errors.mealType && (
                        <p className="text-xs text-destructive">Meal type is required</p>
                    )}
                </div>

                {/* Date and Time Eaten */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="dateEaten" className="text-sm font-medium">
                            Date Eaten
                        </Label>
                        <Input
                            id="dateEaten"
                            type="date"
                            max={today}
                            {...register('dateEaten', {
                                required: 'Choose the day you ate this meal',
                                // YYYY-MM-DD strings compare in calendar order.
                                validate: (value: string) =>
                                    value <= todayLocalDateStr() ||
                                    'Meals cannot be logged for a future date',
                            })}
                        />
                        {errors.dateEaten && (
                            <p className="text-xs text-destructive">
                                {errors.dateEaten?.message as string}
                            </p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="timeEaten" className="text-sm font-medium">
                            Time Eaten
                        </Label>
                        <Input id="timeEaten" {...register('timeEaten')} type="time" />
                    </div>
                </div>

                {/* Meal Source Toggle */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium">Meal Source</Label>
                    <div className="flex space-x-4">
                        <Button
                            type="button"
                            variant={!selectedRecipe ? 'default' : 'outline'}
                            onClick={() => {
                                clearRecipeSelection();
                                setMealSource('CUSTOM');
                            }}
                        >
                            Custom Meal
                        </Button>
                        <Button
                            type="button"
                            variant={selectedRecipe ? 'default' : 'outline'}
                            onClick={() => {
                                setShowRecipeSelector(true);
                                setMealSource('RECIPE');
                            }}
                        >
                            <ChefHat className="w-4 h-4 mr-2" />
                            From Recipe
                        </Button>
                    </div>
                </div>

                {/* Recipe Selector Modal */}
                {showRecipeSelector && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                        onClick={(e) => {
                            // Close modal if clicking the overlay
                            if (e.target === e.currentTarget) {
                                setShowRecipeSelector(false);
                            }
                        }}
                    >
                        <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden">
                            <Card className="p-6 overflow-y-auto max-h-[90vh]">
                                <RecipeSelector
                                    onSelectRecipe={handleRecipeSelect}
                                    onCancel={() => setShowRecipeSelector(false)}
                                />
                            </Card>
                        </div>
                    </div>
                )}

                {/* Selected Recipe Badge */}
                {selectedRecipe && (
                    <div className="bg-brand-header-wash border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <ChefHat className="w-6 h-6 text-brand-text-blue" />
                                <div>
                                    <p className="font-semibold text-foreground">
                                        {selectedRecipe.name}
                                    </p>
                                    <p className="text-sm text-brand-text-blue">
                                        Auto-populated {recipeFoodItems.length} nutrition categories
                                    </p>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={clearRecipeSelection}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Meal Name and Food Items */}
                {!selectedRecipe && (
                    <div className="space-y-2">
                        <Label htmlFor="customMealName" className="text-sm font-medium">
                            Meal Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            {...register('customMealName', { required: 'Meal name is required' })}
                            placeholder="e.g., Grilled chicken salad"
                        />
                        {errors.customMealName && (
                            <p className="text-xs text-destructive">
                                {errors.customMealName?.message as string}
                            </p>
                        )}
                    </div>
                )}

                {/* Food Items Section - Show for both custom and recipe */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">
                            {selectedRecipe ? 'Nutrition from Recipe' : 'Food Items'}
                        </Label>
                        {!selectedRecipe && (
                            <Button type="button" size="sm" onClick={addFoodItem}>
                                <Plus className="w-4 h-4 mr-1" />
                                Add Item
                            </Button>
                        )}
                    </div>

                    {(selectedRecipe ? recipeFoodItems : customFoodItems).map((item, index) => (
                        <Card key={index} className="p-4 bg-muted">
                            <div className="grid grid-cols-12 gap-3 items-end">
                                <div className="col-span-12 sm:col-span-4">
                                    <Label htmlFor={`food-category-${index}`} className="text-xs">
                                        Category
                                    </Label>
                                    <Select
                                        value={item.categoryId}
                                        onValueChange={(value) =>
                                            updateFoodItem(index, 'categoryId', value)
                                        }
                                    >
                                        <SelectTrigger id={`food-category-${index}`}>
                                            <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categoriesLoading ? (
                                                <SelectItem value="loading" disabled>
                                                    Loading categories...
                                                </SelectItem>
                                            ) : categoriesError ? (
                                                <SelectItem value="error" disabled>
                                                    Error loading categories
                                                </SelectItem>
                                            ) : foodCategories.length === 0 ? (
                                                <SelectItem value="no-categories" disabled>
                                                    No categories available
                                                </SelectItem>
                                            ) : (
                                                foodCategories.map((category) => (
                                                    <SelectItem
                                                        key={category.id}
                                                        value={category.id}
                                                    >
                                                        {category.name} ({category.points} pts)
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="col-span-4 sm:col-span-2">
                                    <Label className="text-xs">Servings</Label>
                                    <Input
                                        type="number"
                                        aria-label={`Servings, row ${index + 1}`}
                                        min="0"
                                        max="10"
                                        step="0.5"
                                        value={item.estimatedServings}
                                        onChange={(e) =>
                                            updateFoodItem(
                                                index,
                                                'estimatedServings',
                                                Number(e.target.value),
                                            )
                                        }
                                    />
                                </div>

                                <div className="col-span-6 sm:col-span-5">
                                    <Label className="text-xs">Description</Label>
                                    <Input
                                        placeholder="e.g., Mixed greens, tomatoes"
                                        value={item.description}
                                        onChange={(e) =>
                                            updateFoodItem(index, 'description', e.target.value)
                                        }
                                    />
                                </div>

                                <div className="col-span-2 sm:col-span-1">
                                    {/* A custom meal keeps one row to type into. Recipe
                                        rows can all go: submitting with none left is
                                        blocked with a message instead (#367). */}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        aria-label={`Remove row ${index + 1}`}
                                        onClick={() => removeFoodItem(index)}
                                        disabled={!selectedRecipe && customFoodItems.length === 1}
                                    >
                                        <Minus className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ))}

                    {foodItemsError && (
                        <p role="alert" className="text-sm text-destructive">
                            {foodItemsError}
                        </p>
                    )}

                    <div className="bg-brand-header-wash p-3 rounded-lg">
                        <p className="text-sm font-medium text-brand-text-blue">
                            Estimated Points: {calculateEstimatedPoints()}
                        </p>
                        <p className="text-xs text-brand-text-blue mt-1">
                            Points are calculated based on servings and category values
                        </p>
                    </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                    <Label htmlFor="notes" className="text-sm font-medium">
                        Notes (Optional)
                    </Label>
                    <Textarea
                        {...register('notes')}
                        placeholder="How did this meal make you feel? Any observations?"
                        className="min-h-[80px]"
                    />
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                    {onCancel && (
                        <Button type="button" variant="outline" onClick={onCancel}>
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Log Meal
                    </Button>
                </div>
            </form>
        </Card>
    );
};
